import CloseIcon from '@mui/icons-material/Close'
import { Button, Dialog, DialogActions, DialogContent, FormGroup, IconButton, Stack, Typography } from '@mui/material'
import debounce from 'lodash-es/debounce'
import { useEffect, useMemo, useState } from 'react'
import { Dialogs, useDialogs } from '../Contexts/DialogsContext'
import { useGlobalContext } from '../Contexts/GlobalContext'
import JoinUs from '../Dialogs/Forms/JoinUs'
import { saveUserSettings } from '../globalHeader/functions/ams-functions'
import { Rules, Settings } from '../globalHeader/GlobalHeader'
import { IAppConfig, getAppConfig } from '../globalHeader/services/AppConfigService'
import { setEmotesMuted, useEmotesMuted } from './emotesMuted'
import SettingsSwitch from './SettingsSwitch'
import { ThemePage, ThemesSettings } from './Themes'

const headers: Record<SettingsPage, string> = {
    SETTINGS: 'Settings',
    PIECES: 'Chess Pieces',
    BOARD: 'Board Theme',
    SOUNDS: 'Custom Sound',
}

const closeIconStyles = {
    position: 'absolute',
    right: { xs: '8px', md: '20px' },
    top: { xs: '8px', md: '20px' },
}

const arrowIconStyles = {
    position: 'absolute',
    left: { xs: '8px', md: '20px' },
    top: { xs: '8px', md: '20px' },
}

export type SettingsDialogsProps = {
    fullScreenView?: boolean
}

export enum SettingsPage {
    'SETTINGS' = 'SETTINGS',
    'PIECES' = 'PIECES',
    'BOARD' = 'BOARD',
    'SOUNDS' = 'SOUNDS',
}

function SettingsDialog(props: SettingsDialogsProps) {
    const { fullScreenView } = props
    const { userData, onSettingsChange, setUserData, pvpEmotes } = useGlobalContext()
    const { registerDialog, unregisterDialog } = useDialogs()
    const emotesMuted = useEmotesMuted()
    const [fullScreen, setFullScreen] = useState<boolean>(() => !!document.fullscreenElement)

    // Esc / F11 / system exits change fullscreen without going through the switch
    useEffect(() => {
        const syncFullScreen = () => setFullScreen(!!document.fullscreenElement)
        document.addEventListener('fullscreenchange', syncFullScreen)
        return () => document.removeEventListener('fullscreenchange', syncFullScreen)
    }, [])

    if (!userData) {
        return null
    }

    const settings = userData.game_settings
    const rules = userData.groups.flatMap((group) => group.rules.map((rule) => rule.name)) || []
    const [localSettings, setLocalSettings] = useState(settings)
    const [currentSettingsPage, setCurrentSettingsPage] = useState<SettingsPage>(SettingsPage.SETTINGS)
    const [appConfig, setAppConfig] = useState<IAppConfig | null>(null)

    useEffect(() => {
        const config = getAppConfig()
        setAppConfig(config)
    }, [])

    const settingsEnabled = rules.some((rule) => rule === Rules.FULL_ACCESS || rule === Rules.GAME_SETTINGS)

    // iPhone Safari has no Element.requestFullscreen; fullscreenEnabled is false in iframes that disallow it
    const fullScreenSupported =
        !!document.fullscreenEnabled && typeof document.documentElement.requestFullscreen === 'function'

    const onFullScreenClick = () => {
        const request = document.fullscreenElement
            ? document.exitFullscreen()
            : document.documentElement.requestFullscreen()
        request?.catch((error) => console.warn('Fullscreen toggle failed', error))
    }

    const saveSettings = useMemo(
        () =>
            debounce(async (settings: Settings) => {
                const data = {
                    autoAccept: true,
                    preMove: settings.preMove,
                    autoQueen: settings.autoQueen,
                    dragAndDrop: settings.dragAndDrop,
                    soundOn: settings.soundOn,
                    soundCheckOn: settings.soundCheckOn,
                    soundMateOn: settings.soundMateOn,
                    soundDrawOn: settings.soundDrawOn,
                    soundSmateOn: settings.soundSmateOn,
                    soundMoveOn: settings.soundMoveOn,
                    pieceStyle: settings.pieceStyle,
                    boardStyle: settings.boardStyle,
                    coordinates: settings.coordinates,
                    soundStyle: settings.soundStyle,
                    darkMode: settings.darkMode,
                    friendlyMode: settings.friendlyMode,
                    showLegalMoves: settings.showLegalMoves,
                    engineToggle: settings.engineToggle,
                    evaluationBar: settings.evaluationBar,
                    highlightTopMoves: settings.highlightTopMoves,
                    multipleLines: settings.multipleLines,
                }
                await saveUserSettings(data)
                setLocalSettings(data)
            }, 500),
        [],
    )

    const onSubmitClick = async () => {
        saveSettings(settings)
        setCurrentSettingsPage(SettingsPage.SETTINGS)
    }

    const onToggleChange = (newSettings: Settings) => {
        setSettings(newSettings)
        saveSettings(newSettings)
    }

    const onCancelClick = () => {
        setSettings(localSettings)
        setCurrentSettingsPage(SettingsPage.SETTINGS)
    }

    const onCloseClick = () => {
        if (currentSettingsPage === SettingsPage.SETTINGS) {
            unregisterDialog(Dialogs.SETTINGS)
        }
        if (currentSettingsPage !== SettingsPage.SETTINGS) {
            onCancelClick()
        }
    }

    const openJoinUsForm = () => {
        unregisterDialog(Dialogs.SETTINGS)
        registerDialog(Dialogs.JOIN_US, <JoinUs />)
    }

    const setSettings = (settings) => {
        setUserData({ ...userData, game_settings: settings })
        onSettingsChange(settings)
    }

    const switches = [
        {
            label: 'Coordinates',
            checked: settings.coordinates,
            onChange: () => {
                onToggleChange({ ...settings, coordinates: !settings.coordinates })
            },
        },
        {
            label: 'Sounds',
            checked: settings.soundOn,
            onChange: () => {
                onToggleChange({ ...settings, soundOn: !settings.soundOn })
            },
        },
        {
            label: 'Premove',
            checked: settings.preMove,
            onChange: () => {
                onToggleChange({ ...settings, preMove: !settings.preMove })
            },
        },
        {
            label: 'Autoqueen',
            checked: settings.autoQueen,
            onChange: () => {
                onToggleChange({ ...settings, autoQueen: !settings.autoQueen })
            },
        },
        {
            label: 'Casual Play',
            checked: settings.friendlyMode,
            helpText: 'Hide Ratings',
            onChange: () => {
                onToggleChange({ ...settings, friendlyMode: !settings.friendlyMode })
            },
        },
        ...(pvpEmotes
            ? [
                  {
                      label: 'Mute Emotes',
                      checked: emotesMuted,
                      helpText: "Hide your opponent's emotes",
                      onChange: () => setEmotesMuted(!emotesMuted),
                  },
              ]
            : []),
        ...(!!appConfig?.hideFullScreenToggle || !fullScreenSupported
            ? []
            : [
                  {
                      label: 'Fullscreen',
                      checked: fullScreen,
                      onChange: onFullScreenClick,
                  },
              ]),
        {
            label: `Legacy sounds`,
            checked: settings.soundStyle === 'legacy',
            disabled: !settingsEnabled,
            onChange: () => {
                onToggleChange({
                    ...settings,
                    soundStyle: settings.soundStyle === 'legacy' ? 'default' : 'legacy',
                })
            },
        },
        {
            label: `Show legal moves`,
            checked: settings.showLegalMoves,
            disabled: !settingsEnabled,
            onChange: () => {
                onToggleChange({ ...settings, showLegalMoves: !settings.showLegalMoves })
            },
        },
    ]

    const SettingsContent = (
        <>
            {!fullScreenView && (
                <IconButton onClick={onCloseClick} sx={closeIconStyles}>
                    <CloseIcon />
                </IconButton>
            )}
            <Typography
                variant="h5"
                textTransform="uppercase"
                sx={{
                    borderBottom: '2px solid white',
                    pb: 0.5,
                    mb: currentSettingsPage === SettingsPage.SETTINGS ? 0 : 1,
                }}
            >
                {headers[currentSettingsPage]}
            </Typography>

            <DialogContent
                sx={{
                    p: 0,
                    pb: currentSettingsPage === SettingsPage.SETTINGS ? 0 : fullScreenView ? '60px' : '70px',
                    borderRadius: 0,
                    alignItems: 'unset',
                }}
            >
                {currentSettingsPage === SettingsPage.SETTINGS ? (
                    <FormGroup sx={{ alignItems: 'center' }}>
                        <ThemesSettings settings={settings} setCurrentSettingsPage={setCurrentSettingsPage} />
                        <Stack
                            sx={{
                                width: '100%',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'left',
                            }}
                            direction={'column'}
                        >
                            {switches.map((item) => (
                                <SettingsSwitch
                                    label={item.label}
                                    checked={item.checked}
                                    onChange={item.onChange}
                                    helpText={item.helpText}
                                    key={item.label}
                                    disabled={item.disabled}
                                    onDisabledClick={openJoinUsForm}
                                />
                            ))}
                        </Stack>
                    </FormGroup>
                ) : (
                    <ThemePage
                        settingsEnabled={settingsEnabled}
                        settings={settings}
                        setSettings={setSettings}
                        currentSettingsPage={currentSettingsPage}
                        openJoinUsForm={openJoinUsForm}
                    />
                )}
            </DialogContent>

            {currentSettingsPage !== SettingsPage.SETTINGS && (
                <DialogActions
                    sx={(theme) => ({
                        gap: 1,
                        position: 'absolute',
                        zIndex: 2,
                        bottom: fullScreenView ? 0 : '30px',
                        pb: fullScreenView ? '14px' : '0',
                        left: fullScreenView ? '14px' : '30px',
                        right: fullScreenView ? '14px' : '30px',
                        background: theme.palette.background.paper,
                        boxShadow: `0px -5px 20px 5px ${theme.palette.background.paper}`,
                        mt: 0,
                        // borderRadius: '0 0 16px 16px',
                    })}
                >
                    <Button fullWidth={true} size="large" onClick={onCancelClick} variant="outlined">
                        Cancel
                    </Button>
                    <Button fullWidth={true} size="large" onClick={onSubmitClick} variant="contained">
                        Apply
                    </Button>
                </DialogActions>
            )}
        </>
    )

    if (fullScreenView) {
        return SettingsContent
    }

    return (
        <Dialog
            open={true}
            onClose={(e: React.SyntheticEvent, reason: string) => (reason === 'backdropClick' ? onCloseClick() : null)}
            hideBackdrop
            sx={{
                '&::-webkit-scrollbar': {
                    width: '0',
                },
                '& .MuiDialogContent-root': {
                    borderRadius: '0 !important',
                    overflowX: 'hidden',
                },
                '& .MuiDialog-paper': {
                    width: '100%',
                    maxWidth: '584px',
                    height: { xs: '100dvh', md: 'min(660px, 100% - 32px)' },
                    flexDirection: 'column',
                    margin: { xs: 0, md: '16px' },
                    p: { xs: '14px !important', md: '30px !important' },
                    maxHeight: 'unset',
                    pb: '0',
                    border: '0.25rem solid white',
                },
            }}
        >
            {SettingsContent}
        </Dialog>
    )
}

export default SettingsDialog
